import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  EMPTY_STRING,
  localeStringOptions,
  MESSAGE_TYPE,
  UNQ_PER_AMOUNT,
} from "../../../../common/constants/common.constants";
import { StakingStatus } from "../../../../common/enums/staking.enum";
import { IDashboardItem } from "../../../../common/interfaces/user-dashboard.interface";
import {
  calculateAmountToBeWithdrawn,
  calculateWithdrawnAmount,
  getAmountOfProposalsForRealm,
  getTotalStakedAmount,
} from "../../../../solana/common/helpers/user.dashboard.helpers";
import { getDecimalsPerMint, round } from "../../../../utilities/helpers";
import { createNotification } from "../../../../utilities/notification";
import SkeletonItem from "../../../SkeletonItem/SkeletonItem";
import "./DashboardItem.scss";
import { DashboardItemType } from "../../../../common/enums/dashboard.enums";
import MintSelect from "../../../CreateProposalWizard/CreateProposal/TransferProposal/MintSelect/MintSelect";
import { ITreasuryTokenAccInfo } from "../../../../common/interfaces/clubs.interface";
import { NATIVE_MINT } from "@solana/spl-token";
import { dashboardStore } from "../../../../contexts/dashboardStore";
import { divide } from "mathjs";
import { usdcMint } from "../../../../solana/common/utilities";

const DashboardItem: FC<{
  dashboardItem: IDashboardItem;
}> = ({ dashboardItem }) => {
  const {
    clubs,
    setSplProposals,
    applications,
    deposits,
    setActiveStep,
    withdrawals,
    stakings,
    splProposals,
    invitations,
    treasuryData,
    treasuryTokens,
    treasuryTokensLoading,
  } = dashboardStore();

  const [description, setDescription] = useState(EMPTY_STRING);
  const [loading, setLoading] = useState(true);
  const wallet = useAnchorWallet();
  const [tokensWithWitdrawnAmount, setTokensWithWitdrawnAmount] =
    useState<ITreasuryTokenAccInfo[]>();
  const [tokensWithWitdrawbleAmount, settokensWithWitdrawbleAmount] =
    useState<ITreasuryTokenAccInfo[]>();
  const [tokensWithDepositAmount, settokensWithDepositAmount] =
    useState<ITreasuryTokenAccInfo[]>();

  useEffect(() => {
    void getFunctionForDashboardItem();
    if (!treasuryTokensLoading) {
      void loadAllClubTokensAndSetWithdrawalAmounts();
    }
  }, [
    wallet?.publicKey,
    withdrawals,
    treasuryData,
    dashboardItem,
    treasuryTokensLoading,
    treasuryTokens,
  ]);

  const isTokensRelatedItems = useMemo(() => {
    return (
      dashboardItem.type === DashboardItemType.AvailalbeWithdraw ||
      dashboardItem.type === DashboardItemType.Deposits ||
      dashboardItem.type === DashboardItemType.TotalWithdraw
    );
  }, [dashboardItem.type]);

  const loadAllClubTokensAndSetWithdrawalAmounts = async () => {
    setLoading(true);
    if (
      withdrawals &&
      treasuryData &&
      treasuryTokens &&
      isTokensRelatedItems &&
      wallet
    ) {
      const allUniqueTokensFromTreasury: ITreasuryTokenAccInfo[] = [];
      const specificDashboardItemTokenList: ITreasuryTokenAccInfo[] = [];
      treasuryTokens.forEach((item) => {
        item?.tokenAccounts.forEach((item) => {
          if (
            !allUniqueTokensFromTreasury.find(
              (itemAll) => item.mint.toString() === itemAll.mint.toString()
            )
          ) {
            allUniqueTokensFromTreasury.push(item);
          }
        });
      });
      if (dashboardItem.type === DashboardItemType.TotalWithdraw) {
        allUniqueTokensFromTreasury.forEach((item) => {
          const totalWitdrawnAmount = calculateWithdrawnAmount(
            withdrawals.filter((withdrawal) => withdrawal.hasWithdrawn),
            item.mint
          );
          specificDashboardItemTokenList.push({
            ...item,
            balance: totalWitdrawnAmount,
          });
        });
        setTokensWithWitdrawnAmount(specificDashboardItemTokenList);
      }
      if (
        dashboardItem.type === DashboardItemType.AvailalbeWithdraw &&
        treasuryData
      ) {
        for (const uniqueToken of allUniqueTokensFromTreasury) {
          let availableWithdrawalAmount = 0;
          for (const treasury of treasuryData) {
            const amountToBeWithdrawnForOneClubAndOneToken =
              calculateAmountToBeWithdrawn(
                withdrawals.filter(
                  (item) =>
                    item.withdrawalMint.toString() ===
                      uniqueToken.mint.toString() &&
                    !item.hasWithdrawn &&
                    item.treasury.toString() ===
                      treasury?.treasuryAddress.toString()
                ),
                treasury?.denominatedTokenInfo,
                deposits!,
                treasury?.reservedRights,
                wallet!.publicKey
              );
            availableWithdrawalAmount =
              +availableWithdrawalAmount +
              amountToBeWithdrawnForOneClubAndOneToken;
          }
          specificDashboardItemTokenList.push({
            ...uniqueToken,
            balance: availableWithdrawalAmount,
          });
        }

        settokensWithWitdrawbleAmount(specificDashboardItemTokenList);
      }

      if (
        dashboardItem.type === DashboardItemType.Deposits &&
        treasuryData &&
        deposits
      ) {
        for (const uniqueToken of allUniqueTokensFromTreasury) {
          let depositAmount = 0;
          for (const treasury of treasuryData) {
            if (
              treasury &&
              (treasury?.denominatedTokenInfo?.mint.toString() ===
                uniqueToken.mint.toString() ||
                (treasury?.denominatedTokenInfo === undefined &&
                  uniqueToken.mint.toString() === NATIVE_MINT.toString()))
            ) {
              const deposit =
                deposits.find(
                  (item) =>
                    item.treasury.toString() ===
                    treasury.treasuryAddress.toString()
                )?.depositAmount ?? 0;
              depositAmount =
                depositAmount +
                divide(deposit, getDecimalsPerMint(uniqueToken));
            }
          }
          specificDashboardItemTokenList.push({
            ...uniqueToken,
            balance: depositAmount,
          });
        }

        settokensWithDepositAmount(specificDashboardItemTokenList);
      }
      //TODO@milica-monke-merge: This case is only for monke, for main use only setDescription from else
      if (dashboardItem.type === DashboardItemType.Deposits) {
        setDescription(
          `${
            specificDashboardItemTokenList
              .find((item) => item.mint.toString() === usdcMint.toString())
              ?.balance.toLocaleString(undefined, localeStringOptions) ?? "0"
          } USDC`
        );
      } else {
        setDescription(
          specificDashboardItemTokenList
            .find((item) => item.mint.toString() === usdcMint.toString())
            ?.balance.toLocaleString(undefined, localeStringOptions) ?? "0"
        );
      }
    }
    setLoading(false);
  };

  const getFunctionForDashboardItem = async () => {
    switch (dashboardItem.type) {
      case DashboardItemType.ActiveProposals:
        await getProposalsForRealm();
        return;
      case DashboardItemType.Clubs:
        setDescription(`${clubs?.length.toString() ?? EMPTY_STRING} Clubs`);
        break;
      case DashboardItemType.StakedAmount:
        getTotalStakedAmountForMember();
        setLoading(false);
        return;
      case DashboardItemType.Invitations:
        const applicationsAmount = applications!.length;
        const invitationsAmount = invitations!.length;
        setDescription((invitationsAmount + applicationsAmount).toString());
        break;
      case DashboardItemType.Reward:
        getTotalRewardForMember();
        setLoading(false);
        return;
    }
  };

  const getProposalsForRealm = useCallback(async () => {
    try {
      if (!splProposals) {
        setLoading(true);
        const proposals = await getAmountOfProposalsForRealm(clubs!);
        setSplProposals(proposals);
        setDescription(proposals.length.toString());
        setLoading(false);
      } else {
        setDescription(splProposals.length.toString());
        setLoading(false);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Problem loading proposals");
    }
  }, [clubs]);

  const getTotalStakedAmountForMember = useCallback(() => {
    setDescription(`${getTotalStakedAmount(stakings!)} UNQ`);
  }, [stakings]);

  const getTotalRewardForMember = useCallback(() => {
    let totalReward = 0;
    stakings!.forEach((item) => {
      if (
        item.memberStakingInfo &&
        item.memberStakingInfo.hasClaimed &&
        item.stakingStatus === StakingStatus.StakingPeriodOver
      ) {
        totalReward =
          totalReward +
          (item.memberStakingInfo.claimAmount * UNQ_PER_AMOUNT -
            item.memberStakingInfo.depositAmount * UNQ_PER_AMOUNT);
      }
    });
    totalReward = round(totalReward / UNQ_PER_AMOUNT, 6);
    setDescription(`${totalReward} UNQ`);
  }, [stakings]);

  const setChosenCurrencyAndUpdateDescription = (ta: ITreasuryTokenAccInfo) => {
    setDescription(ta.balance.toLocaleString(undefined, localeStringOptions));
  };

  return (
    <div className="dashboard-item">
      <div className="dashboard-item__icon">
        <dashboardItem.icon />
      </div>
      <div className="dashboard-item__title-description">
        <p className="dashboard-item__title">{dashboardItem.title}</p>
        <div className="dashboard-item__descirption-wrapper">
          <p className="dashboard-item__description">
            {(!loading && !isTokensRelatedItems) ||
            (isTokensRelatedItems && !treasuryTokensLoading) ? (
              description
            ) : (
              <SkeletonItem height={28} width={100} borderRadius={8} />
            )}
          </p>
          {treasuryData &&
            dashboardItem.type === DashboardItemType.TotalWithdraw &&
            tokensWithWitdrawnAmount &&
            tokensWithWitdrawnAmount.length > 0 && (
              <MintSelect
                treasuryTokenAccounts={tokensWithWitdrawnAmount}
                setChosenCurrency={setChosenCurrencyAndUpdateDescription}
                chosenMint={NATIVE_MINT.toString()}
              />
            )}
          {treasuryData &&
            dashboardItem.type === DashboardItemType.AvailalbeWithdraw &&
            tokensWithWitdrawbleAmount &&
            tokensWithWitdrawbleAmount.length > 0 && (
              <MintSelect
                treasuryTokenAccounts={tokensWithWitdrawbleAmount}
                setChosenCurrency={setChosenCurrencyAndUpdateDescription}
                chosenMint={NATIVE_MINT.toString()}
              />
            )}

          {/* //TODO@milica-monke-merge: This is removed only for monke, for main instance uncomment this
          {treasuryData &&
            dashboardItem.type === DashboardItemType.Deposits &&
            tokensWithDepositAmount &&
            tokensWithDepositAmount.length > 0 && (
              <MintSelect
                treasuryTokenAccounts={tokensWithDepositAmount}
                setChosenCurrency={setChosenCurrencyAndUpdateDescription}
                //TODO@milica-gilder:check with product what is default for mint
                chosenMint={undefined}
              />
            )} */}
        </div>
      </div>
      <button
        onClick={() => setActiveStep(dashboardItem.step)}
        className="dashboard-item__details"
      >
        View details
      </button>
    </div>
  );
};

export default DashboardItem;
